import createClient, { type Middleware } from "openapi-fetch";

import { API_BASE_URL } from "../config/env";
import { ApiError, toApiError } from "./errors";
import type { paths } from "./schema.d.ts";

/** 非 2xx 一律规范化为 ApiError 抛出，hooks 层不再处理 { data, error } 双返回。 */
const errorMiddleware: Middleware = {
  async onResponse({ response }) {
    if (!response.ok) {
      let body: unknown;
      try {
        body = await response.clone().json();
      } catch {
        body = undefined;
      }
      throw toApiError(response.status, body);
    }
    return response;
  },
};

let csrfTokenInMemory: string | undefined;

/**
 * 每次请求时再取 globalThis.fetch。openapi-fetch 默认在 createClient 时捕获 fetch，
 * 测试里 msw 的 listen 发生在模块求值之后，绑死的引用会绕过拦截打到真实网络。
 */
export const api = createClient<paths>({
  baseUrl: API_BASE_URL,
  fetch: (...args: Parameters<typeof fetch>) => apiFetch(...args),
});
api.use(errorMiddleware);

/** Every API request carries the HttpOnly session cookie; unsafe requests also double-submit CSRF. */
export function apiFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  return fetchWithCsrfRecovery(input, init, true);
}

async function fetchWithCsrfRecovery(input: RequestInfo | URL, init: RequestInit, allowRecovery: boolean): Promise<Response> {
  const response = await fetchWithCsrfToken(input, init);
  if (!allowRecovery || response.status !== 403 || !(await isCsrfInvalid(response))) return response;
  if (!(await refreshCsrfToken())) return response;
  return fetchWithCsrfRecovery(input, init, false);
}

async function fetchWithCsrfToken(input: RequestInfo | URL, init: RequestInit): Promise<Response> {
  const headers = new Headers(init.headers);
  const method = (init.method ?? "GET").toUpperCase();
  if (!["GET", "HEAD", "OPTIONS"].includes(method) && typeof document !== "undefined") {
    const csrfToken = csrfTokenInMemory ?? readCookie("ecomgen_csrf");
    if (csrfToken && !headers.has("x-csrf-token")) headers.set("x-csrf-token", csrfToken);
  }
  // Request body 流只能消费一次；CSRF 恢复可能重试同一个请求，必须为每次发送提供副本。
  const request = input instanceof Request ? input.clone() : input;
  return globalThis.fetch(request, { ...init, headers, credentials: "include" });
}

async function isCsrfInvalid(response: Response): Promise<boolean> {
  try {
    const payload = await response.clone().json() as { code?: unknown; error?: { code?: unknown } };
    return payload.code === "CSRF_INVALID" || payload.error?.code === "CSRF_INVALID";
  } catch {
    return false;
  }
}

async function refreshCsrfToken(): Promise<boolean> {
  const response = await globalThis.fetch(`${API_BASE_URL}/auth/session`, { credentials: "include" });
  if (!response.ok) {
    if (response.status === 401) clearCsrfToken();
    return false;
  }
  try {
    const payload = await response.json() as { csrfToken?: unknown };
    if (typeof payload.csrfToken !== "string" || payload.csrfToken.length === 0) return false;
    setCsrfToken(payload.csrfToken);
    return true;
  } catch {
    return false;
  }
}

/** Store the token returned by the API so a separately hosted Web app can write safely. */
export function setCsrfToken(token: string | undefined): void {
  csrfTokenInMemory = token || undefined;
}

export function clearCsrfToken(): void {
  csrfTokenInMemory = undefined;
}

/** Use credentials only for files served by this API; external CDN URLs remain anonymous. */
export function fetchAsset(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (isApiResourceUrl(input)) return apiFetch(input, init);
  return init === undefined ? globalThis.fetch(input) : globalThis.fetch(input, init);
}

function isApiResourceUrl(input: RequestInfo | URL): boolean {
  if (input instanceof Request) return isApiResourceUrl(input.url);
  const value = input instanceof URL ? input.href : input;
  if (typeof value !== "string") return false;
  if (value.startsWith("/")) return true;
  try {
    const target = new URL(value);
    const base = new URL(API_BASE_URL, typeof window === "undefined" ? "http://localhost" : window.location.origin);
    return target.origin === base.origin && target.pathname.startsWith(`${base.pathname}/`);
  } catch {
    return false;
  }
}

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  for (const cookie of document.cookie.split(";")) {
    const item = cookie.trim();
    if (item.startsWith(prefix)) return item.slice(prefix.length);
  }
  return undefined;
}

/**
 * 成功响应按契约必有 body；204/205 的空 body 是删除类端点的正常语义，其余缺失说明契约漂移，显式失败而非静默 undefined。
 * 同时接受已 resolve 的结果对象：调用方需要在同一个结果上读 response.status（如区分「复用既有任务」与「新建任务」）时不必再包一层 Promise。
 */
export async function unwrap<T>(result: Promise<{ data?: T; error?: unknown; response: Response }> | { data?: T; error?: unknown; response: Response }): Promise<T> {
  const { data, response } = await result;
  if (data === undefined) {
    if (response.status !== 204 && response.status !== 205) {
      throw new ApiError({ code: "UNKNOWN", message: "响应缺少数据", status: response.status });
    }
    // 204/205 无 body 属正常删除语义，调用方以 void 消费返回值。
    return undefined as T;
  }
  return data;
}
